import React, {useEffect,useState,useCallback,useMemo} from "react";
import "../Style/UserList.css";
import { downloadPdfBlob } from "../utilities/downloadPdf";
import { COURSE_PLAN_LABEL, getCoursePlan, isSafeHttpUrl } from "../utilities/coursePlan";
import CoursePlanLink from "./CoursePlanLink";

const MONTHS_HY = [
    "Հունվար","Փետրվար","Մարտ","Ապրիլ",
    "Մայիս","Հունիս","Հուլիս","Օգոստոս",
    "Սեպտեմբեր","Հոկտեմբեր","Նոյեմբեր","Դեկտեմբեր",
];

const UsersList = ({ status }) => {
    const [users, setUsers]             = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterMonth, setFilterMonth] = useState("");  // "1"–"12" or ""
    const [filterYear, setFilterYear]   = useState("");  // "2025" or ""
    const [expandedUser, setExpandedUser] = useState(null);
    const [loading, setLoading]         = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [cache, setCache]             = useState({});
    const usersPerPage = 30;
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5002";

    /* ===================== HELPERS ===================== */

    const formatDate = (dateString) => {
        if (!dateString) return "Անհասանելի";
        const d = new Date(dateString);
        return isNaN(d.getTime()) ? "Անհասանելի" : d.toISOString().slice(0, 10);
    };

    /* ===================== FETCH USERS ===================== */

    const fetchUsers = useCallback(async () => {
        const cacheKey = `users-${status}`;
        if (cache[cacheKey]) {
            setUsers(cache[cacheKey]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const controller = new AbortController();
            const timeoutId  = setTimeout(() => controller.abort(), 10000);
            const token      = localStorage.getItem("token");

            const res = await fetch(
                `${API_URL}/users-paddingadmin?status=${encodeURIComponent(status)}`,
                {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            clearTimeout(timeoutId);

            if (!res.ok) throw new Error("Fetch failed");

            const data   = await res.json();
            const sorted = [...data].sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );

            setCache((p) => ({ ...p, [cacheKey]: sorted }));
            setUsers(sorted);
        } catch (e) {
            console.error("Fetch users error:", e);
        } finally {
            setLoading(false);
        }
    }, [API_URL, status, cache]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    /* ===================== UNIQUE YEARS from data ===================== */

    const availableYears = useMemo(() => {
        const set = new Set();
        users.forEach((u) => {
            if (u.created_at) {
                const y = new Date(u.created_at).getFullYear();
                if (!isNaN(y)) set.add(y);
            }
        });
        return Array.from(set).sort((a, b) => b - a);
    }, [users]);

    /* ===================== COMBINED FILTER ===================== */

    const filteredUsers = useMemo(() => {
        const q  = searchQuery.toLowerCase().trim();
        const mo = filterMonth ? parseInt(filterMonth, 10) : null;
        const yr = filterYear  ? parseInt(filterYear, 10)  : null;

        return users.filter((u) => {
            // Name search
            if (q) {
                const name = `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase();
                if (!name.includes(q)) return false;
            }

            // Month / year filter on created_at
            if (mo !== null || yr !== null) {
                if (!u.created_at) return false;
                const d = new Date(u.created_at);
                if (isNaN(d.getTime())) return false;
                if (mo !== null && d.getMonth() + 1 !== mo) return false;
                if (yr !== null && d.getFullYear()     !== yr) return false;
            }

            return true;
        });
    }, [users, searchQuery, filterMonth, filterYear]);

    // Reset to page 1 whenever filtered set changes
    useEffect(() => { setCurrentPage(1); }, [filteredUsers.length]);

    const hasActiveFilter = searchQuery || filterMonth || filterYear;

    const resetFilters = () => {
        setSearchQuery("");
        setFilterMonth("");
        setFilterYear("");
    };

    /* ===================== VIEW TOGGLE ===================== */

    const handleViewMore = useCallback((id) => {
        setExpandedUser((p) => (p === id ? null : id));
    }, []);

    /* ===================== PDF DOWNLOAD ===================== */

    const handleDownloadPdf = async (userId) => {
        if (!userId) { alert("PDF ֆայլը հասանելի չէ"); return; }
        try {
            const token = localStorage.getItem("token");
            const res   = await fetch(`${API_URL}/users/${userId}/pdf`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Download failed");
            const blob = await res.blob();
            downloadPdfBlob(blob, `agreement-${userId}.pdf`);
        } catch (err) {
            console.error(err);
            alert("Չհաջողվեց ներբեռնել PDF ֆայլը");
        }
    };

    /* ===================== PAGINATION ===================== */

    const totalPages   = Math.max(1, Math.ceil(filteredUsers.length / usersPerPage));
    const currentUsers = useMemo(() => {
        const first = (currentPage - 1) * usersPerPage;
        return filteredUsers.slice(first, first + usersPerPage);
    }, [currentPage, filteredUsers]);

    /* ===================== RENDER ===================== */
    return (
        <div className="users-list">
            <div className="users-header">
                <h2 className="users-title">
                    {status === "approved" ? "Ընդունված օգտատերեր" : "Մերժված օգտատերեր"}
                </h2>
                {hasActiveFilter && (
                    <span className="users-result-count">
                        {filteredUsers.length} / {users.length}
                    </span>
                )}
            </div>

            {/* ── Filter bar ── */}
            <div className="users-filter-bar">
                <input
                    type="text"
                    className="users-search"
                    placeholder="Որոնել անունով կամ ազգանունով..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />

                <select
                    className="users-filter-select"
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                    aria-label="Ֆիլտրել ըստ ամսի"
                >
                    <option value="">Բոլոր ամիսները</option>
                    {MONTHS_HY.map((name, i) => (
                        <option key={i + 1} value={i + 1}>{name}</option>
                    ))}
                </select>

                <select
                    className="users-filter-select"
                    value={filterYear}
                    onChange={(e) => setFilterYear(e.target.value)}
                    aria-label="Ֆիլտրել ըստ տարվա"
                >
                    <option value="">Բոլոր տարիները</option>
                    {availableYears.map((y) => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>

                {hasActiveFilter && (
                    <button className="users-filter-reset" onClick={resetFilters}>
                        ✕ Մաքրել
                    </button>
                )}
            </div>

            {/* ── Results ── */}
            {loading ? (
                <p>Բեռնվում է...</p>
            ) : currentUsers.length === 0 ? (
                <p>
                    {hasActiveFilter
                        ? "Ֆիլտրի արդյունքում գրառումներ չկան"
                        : "Օգտատերեր չեն գտնվել"}
                </p>
            ) : (
                <>
                    <ul className="users-container">
                        {currentUsers.map((user) => (
                            <li key={user.id} className="user-item">
                                <span className="user-info">
                                    {user.first_name}{" "}
                                    {user.last_name} — {user.email}
                                </span>

                                <button
                                    className="user-action-btn"
                                    onClick={() => handleViewMore(user.id)}
                                >
                                    {expandedUser === user.id ? "Փակել" : "Տեսնել ավելի"}
                                </button>

                                {expandedUser === user.id && (
                                    <div className="user-details">
                                        <p>Անուն: {user.first_name}</p>
                                        <p>Ազգանուն: {user.last_name}</p>
                                        <p>Հայրանուն: {user.patronymic || "Անհասանելի"}</p>
                                        <p>Հեռախոս: {user.phone}</p>
                                        <p>Email: {user.email}</p>
                                        <p>Դասընթաց: {user.selected_course}</p>
                                        {isSafeHttpUrl(getCoursePlan(user)) && (
                                            <p>
                                                {COURSE_PLAN_LABEL}:{" "}
                                                <CoursePlanLink href={getCoursePlan(user)}>
                                                    Դիտել
                                                </CoursePlanLink>
                                            </p>
                                        )}
                                        <p>
                                            Վճարման եղանակ:{" "}
                                            {user.payment_method === "full"
                                                ? "Միանվագ"
                                                : user.payment_method === "monthly"
                                                    ? "Մաս մաս"
                                                    : user.payment_method === "paylater"
                                                        ? "Հետվճարային"
                                                        : "Անհասանելի"}
                                        </p>
                                        <p>Ընդհանուր գին: {user.total_price} դրամ</p>
                                        <p>Կարգավիճակ: {user.status}</p>
                                        <p>Կնքման ամսաթիվ: {formatDate(user.created_at)}</p>

                                        {user.pdf_path && (
                                            <button
                                                className="user-action-btn"
                                                style={{ marginTop: 12 }}
                                                onClick={() => handleDownloadPdf(user.id)}
                                            >
                                                ⬇️ Ներբեռնել PDF
                                            </button>
                                        )}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>

                    <div className="pagination">
                        <button
                            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                            disabled={currentPage === 1}
                        >
                            ← Նախորդ
                        </button>
                        <span>Էջ {currentPage} / {totalPages}</span>
                        <button
                            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                            disabled={currentPage === totalPages}
                        >
                            Հաջորդ →
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default UsersList;
